/**
 * Quiz — "Find My Match" / RESULTS page
 * -----------------------------------------------------------------------------
 * STANDALONE script. NOT a webpack build output — hand-authored and safe to edit
 * directly. Kept out of dev/src/js/sections/ (webpack auto-globs it) so adding
 * the results page never forces a rebuild of vendor.js / common.js / theme.js.
 *
 * FORK of assets/quiz-results.js (the "Find Your Ritual" results page), which
 * stays untouched — it is live. Same mechanism throughout; what changes:
 *   - the sessionStorage key: `froya:quiz:match:v1` (assets/quiz-match.js)
 *   - the routing rule: Q1 `focus` (skin / hair / both) -> bundle, instead of
 *     a Q2 concern map
 *   - the section keys revealed per bundle (five per bundle, not two)
 *   - no age band, empathy line, routine line or concern bullets — this quiz
 *     asks none of those and the design carries none of them
 *   - NO DISCOUNT LAYER. The ritual quiz tags every add-to-cart with
 *     `_froya_bundle_id` / `_froya_sub_bundle_id` so the Bundle Discounts app
 *     takes the quiz price off. This quiz gives no quiz-only discount — the
 *     three results sell at their normal price, and the bundle result is the
 *     two systems at their combined price — so the promo tagging, the drawer
 *     quick-add tagging and the promo-config JSON are all gone. Do not re-add
 *     them without a discount to apply: they would tag lines the Bundle
 *     Discounts app has no matching row for.
 *
 * Paired with: sections/quiz-match-results-header.liquid,
 *              sections/quiz-match-whats-in-it.liquid,
 *              assets/quiz-match-results.css
 *
 * WHY sessionStorage: the results page is a SEPARATE page load from the quiz,
 * so nothing can be handed over in memory. A visitor without a `completedAt`
 * in that record never reaches this script — the inline gate in the header
 * section redirects them to the quiz first.
 *
 * WHAT IT DOES: nothing is built at runtime. Every copy variant is already in
 * the DOM, server-rendered and hidden by Liquid; this only reveals the matching
 * one. No flash of the wrong variant, and the copy stays in Liquid.
 *
 * Public surface:
 *   window.FroyaMatchResults.getAnswers()  -> { focus: 'skin', concerns: [...] }
 *   window.FroyaMatchResults.getIdentity() -> { first_name, ... }
 *   window.FroyaMatchResults.getBundle()   -> 'mature_skin' | 'mature_hair' | 'mature_skin_hair'
 *   document 'quiz-match-results:ready'    -> { answers, identity, bundle }
 */
(function () {
  'use strict';

  var STORAGE_VERSION = 1;
  var DEFAULT_KEY = 'froya:quiz:match:v1';

  /**
   * Q1 -> recommended bundle. The whole routing rule of this quiz. MUST match
   * BUNDLE_FOR_FOCUS in assets/quiz-match.js, which writes the same value to
   * Klaviyo at email submit.
   */
  var BUNDLE_BY_FOCUS = {
    skin: 'mature_skin',
    hair: 'mature_hair',
    both: 'mature_skin_hair'
  };

  var DEFAULT_BUNDLE = 'mature_skin';

  /* ---------------------------------------------------------------- state -- */

  function readState(key) {
    var raw = null;
    try {
      raw = window.sessionStorage.getItem(key);
    } catch (e) {
      /* private mode / storage disabled — fall through to the defaults */
    }
    if (!raw) return null;

    var saved;
    try {
      saved = JSON.parse(raw);
    } catch (e) {
      return null;
    }
    // Version guard mirrors the quiz's own: a shape written by an older build
    // is ignored rather than half-read into the wrong slots.
    if (!saved || saved.v !== STORAGE_VERSION) return null;
    return saved;
  }

  /* ----------------------------------------------------------------- dom -- */

  function reveal(root, attr, value) {
    var nodes = root.querySelectorAll('[' + attr + ']');
    var matched = false;

    for (var i = 0; i < nodes.length; i++) {
      var own = nodes[i].getAttribute(attr);
      var on = own === value;
      nodes[i].hidden = !on;
      if (on) matched = true;
    }

    // Nothing matched (answer missing, or a slug the markup does not carry) —
    // fall back so the sentence still reads instead of collapsing to nothing.
    if (!matched) {
      for (var j = 0; j < nodes.length; j++) {
        nodes[j].hidden = nodes[j].getAttribute(attr) !== 'fallback';
      }
    }
    return matched;
  }

  function indexOf(arr, value) {
    for (var i = 0; i < arr.length; i++) {
      if (arr[i] === value) return i;
    }
    return -1;
  }

  /**
   * The template's section keys, per bundle: one buy box and one stats section
   * each. All three of each are rendered and the matching pair is revealed —
   * the pattern page.quiz.json already uses in production, so market-correct
   * pricing is server-rendered for free and only visibility changes here.
   *
   * SAFE BECAUSE the bundles are single-variant with no quantity selector.
   * The buy box carries hardcoded ids (#purchase-type-fieldset, #custom-product-form)
   * and _component-product-form-new.js has ~19 document-level querySelectors,
   * so three copies do collide — but only inside its variant/quantity/currency
   * UPDATE paths, which these products never trigger. The purchase-type toggle
   * is scoped via closest('c-subscription'), so switching is safe.
   * If a bundle ever gains real variants or show_quantity_selectors is turned
   * on here, updatePriceBlock() starts writing to the FIRST .js-price-block on
   * the page — i.e. the wrong bundle's price. Don't do that without moving to
   * one-buy-box-per-template (?view=).
   */

  var SECTION_KEYS_BY_BUNDLE = {
    mature_skin:      ['bundle_skin', 'whatsinit_skin', 'reviews_skin', 'stats_skin', 'faq_skin'],
    mature_hair:      ['bundle_hair', 'whatsinit_hair', 'reviews_hair', 'stats_hair', 'faq_hair'],
    mature_skin_hair: ['bundle_both', 'whatsinit_both', 'reviews_both', 'stats_both', 'faq_both']
  };

  var VISIBLE_CLASS = 'is-quiz-bundle-visible';

  /**
   * A JSON template renders sections as `shopify-section-template--<hash>__<key>`,
   * and a template cannot put attributes on that wrapper — so sections are
   * addressed by key suffix. Same resolver as section-quiz-funnel.js, including
   * the plain `shopify-section-<key>` fallback for a statically rendered section.
   */
  function findSectionEl(key) {
    return document.querySelector('[id^="shopify-section"][id$="__' + key + '"]')
      || document.getElementById('shopify-section-' + key);
  }

  /**
   * The buy-box anchor. Every lp-main-product renders
   * `<div class="s-main-product__container" id="{{ anchor_id }}">` (default
   * "product-section"), so with three buy boxes on the page there are three
   * identical ids and document.getElementById() — which is what
   * __froyaScrollTo (snippets/lp-smooth-scroll.liquid) uses first — returns
   * the FIRST one, i.e. bundle_mature, even when it is display:none. A hidden
   * element has a zero rect, so every "scroll to the buy box" CTA lower on the
   * page would scroll nowhere for acne / pharmacy. Fix: keep the id only on
   * the visible bundle's container; park it on the hidden ones so a re-run
   * (bundle change) can restore it.
   */
  var ANCHOR_PARK_ATTR = 'data-quiz-parked-anchor';

  function syncBuyBoxAnchor(sectionEl, visible) {
    var container = sectionEl.querySelector('.s-main-product__container');
    if (!container) return; // stats sections have no buy box
    if (visible) {
      if (!container.id && container.getAttribute(ANCHOR_PARK_ATTR)) {
        container.id = container.getAttribute(ANCHOR_PARK_ATTR);
      }
    } else if (container.id) {
      container.setAttribute(ANCHOR_PARK_ATTR, container.id);
      container.removeAttribute('id');
    }
  }

  function revealBundle(bundle) {
    var wanted = SECTION_KEYS_BY_BUNDLE[bundle] || [];

    for (var key in SECTION_KEYS_BY_BUNDLE) {
      if (!Object.prototype.hasOwnProperty.call(SECTION_KEYS_BY_BUNDLE, key)) continue;
      var keys = SECTION_KEYS_BY_BUNDLE[key];
      for (var i = 0; i < keys.length; i++) {
        var el = findSectionEl(keys[i]);
        if (!el) continue;
        var visible = indexOf(wanted, keys[i]) !== -1;
        if (visible) el.classList.add(VISIBLE_CLASS);
        else el.classList.remove(VISIBLE_CLASS);
        syncBuyBoxAnchor(el, visible);
      }
    }
  }

  /**
   * Order attribution + completion event, via window.FroyaQuiz
   * (snippets/quiz-analytics.liquid). Done HERE, on the results page itself:
   * Shopify files these orders under the page the visit started on (often
   * /pages/hair-quiz), so the cart attribute is the only record that this quiz
   * sold them. The helper also re-checks the cart after an add. quiz_completed
   * fires once per completion, not on every reload. Its own try/catch: a throw
   * must not reach start()'s fallback, which would swap in the default bundle.
   */
  function reportResult(bundle, answers, completedAt) {
    try {
      var quiz = window.FroyaQuiz;
      if (!quiz) return;
      var quizId = quiz.IDS.MATCH;
      quiz.tagCart(quizId, bundle);
      if (quiz.once(quizId, 'quiz_completed', completedAt || 'unknown')) {
        quiz.track('quiz_completed', { quiz_id: quizId, result: bundle, answers: answers });
      }
    } catch (e) {
      /* analytics must never break the results page */
    }
  }

  /* ----------------------------------------------------------------- run -- */

  function init() {
    var root = document.querySelector('[data-quiz-results]');
    if (!root) return;

    var saved = readState(root.dataset.storageKey || DEFAULT_KEY) || {};
    var answers = saved.answers && typeof saved.answers === 'object' ? saved.answers : {};
    var identity = saved.identity && typeof saved.identity === 'object' ? saved.identity : {};

    // Greeting. A blank or whitespace-only name counts as no name, so the
    // comma-first variant can never render as ", your results are in".
    var name = (identity.first_name || '').trim();
    var nameSlot = root.querySelector('[data-quiz-results-name]');
    if (name && nameSlot) {
      nameSlot.textContent = name;
      reveal(root, 'data-quiz-results-greeting', 'named');
    } else {
      reveal(root, 'data-quiz-results-greeting', 'anon');
    }

    var bundle = BUNDLE_BY_FOCUS[answers.focus] || DEFAULT_BUNDLE;
    revealBundle(bundle);
    reportResult(bundle, answers, saved.completedAt);

    window.FroyaMatchResults = {
      getAnswers: function () { return JSON.parse(JSON.stringify(answers)); },
      getIdentity: function () { return JSON.parse(JSON.stringify(identity)); },
      getBundle: function () { return bundle; }
    };

    document.dispatchEvent(new CustomEvent('quiz-match-results:ready', {
      detail: { answers: answers, identity: identity, bundle: bundle }
    }));
  }

  /**
   * A thrown error would leave every bundle hidden — the page would have no buy
   * box at all, because the reveal is what makes one visible. So a failure falls
   * back to the default bundle rather than an empty page. This is a real guard,
   * not a timing workaround: it runs once, immediately, on the error path only.
   */
  function start() {
    try {
      init();
    } catch (e) {
      try {
        revealBundle(DEFAULT_BUNDLE);
      } catch (e2) {
        /* nothing further we can do client-side; <noscript> covers no-JS */
      }
      if (window.console && console.warn) {
        console.warn('[quiz-match-results] personalisation failed, showing the default bundle', e);
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
